"use client";

import { useCallback, useEffect, useId, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  loadNotificationsClient,
  markNotificationReadClient,
  type ApiNotificationItem,
} from "@/lib/notificationsClient";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export type NotificationsBellProps = {
  /** `nav` = header bar; `hero` = overlay on home hero (legacy). */
  variant?: "nav" | "hero";
};

export function NotificationsBell({ variant = "nav" }: NotificationsBellProps) {
  const { user, isLoggedIn, isReady } = useAuth();
  const { t, locale } = useLanguage();
  const dialogTitleId = useId();
  const lang: "en" | "ar" = locale === "ar" ? "ar" : "en";

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ApiNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customerId = user?.customerId?.trim() ?? "";

  const refreshList = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, ok, message } = await loadNotificationsClient({
        customerId,
        page: 1,
        offset: 10,
        languageCode: lang,
      });
      if (!ok || !data) {
        setError(message?.trim() || t("notifications.loadError"));
        setItems([]);
        setUnreadCount(0);
        return;
      }
      setItems(data.notifications ?? []);
      setUnreadCount(Number(data.unread_count ?? 0));
    } catch {
      setError(t("notifications.loadError"));
    } finally {
      setLoading(false);
    }
  }, [customerId, lang, t]);

  useEffect(() => {
    if (!isReady || !isLoggedIn || !customerId) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      void (async () => {
        const { data, ok } = await loadNotificationsClient({
          customerId,
          page: 1,
          offset: 5,
          languageCode: lang,
        });
        if (cancelled) return;
        if (ok && data) {
          setUnreadCount(Number(data.unread_count ?? 0));
        } else {
          setUnreadCount(0);
        }
      })();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [isReady, isLoggedIn, customerId, lang]);

  useEffect(() => {
    if (!open || !customerId) return;
    const fr = requestAnimationFrame(() => {
      void refreshList();
    });
    return () => cancelAnimationFrame(fr);
  }, [open, customerId, refreshList]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!isReady || !isLoggedIn) return null;

  const handleRowActivate = async (n: ApiNotificationItem) => {
    const id = n.uuid?.trim();
    if (!id || !customerId) return;
    if (Number(n.is_read) === 1) return;
    const ok = await markNotificationReadClient(customerId, id);
    if (ok) {
      setItems((prev) =>
        prev.map((x) => (x.uuid === id ? { ...x, is_read: 1 } : x))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  };

  const wrapperClass =
    variant === "hero"
      ? "absolute top-4 end-4 sm:end-6 md:top-6 md:end-8 z-[25] pointer-events-none [&>*]:pointer-events-auto"
      : "relative shrink-0";

  const buttonClass =
    variant === "hero"
      ? "relative flex items-center justify-center w-11 h-11 rounded-full bg-white/95 backdrop-blur-sm border border-white/50 text-[#130F0E] shadow-lg hover:bg-white hover:text-[#BD2226] transition-colors"
      : "relative flex items-center justify-center w-10 h-10 rounded-full border border-[#E5E5E5] bg-white text-[#130F0E] shadow-sm hover:border-[#DD0428]/40 hover:text-[#BD2226] transition-colors";

  return (
    <>
      <div className={wrapperClass}>
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            PushDataToGTM(GTMEvents.DataLayerUpdate, {
              pageName: "DESKTOP HEADER",
              pageCategory: "DESKTOP HEADER",
              eventCategory: GTMSubEvents.Click,
              eventLabel: "DESKTOP HEADER > NOTIFICATIONS BELL CLICKED",
            });
          }}
          className={buttonClass}
          aria-label={t("notifications.open")}
        >
          <BellIcon className="w-5 h-5" />
          {unreadCount > 0 ? (
            <span className="absolute -top-0.5 -end-0.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-[#BD2226] text-[10px] font-bold text-white tabular-nums">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          ) : null}
        </button>
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-[200] flex items-start justify-center sm:items-center p-4 bg-[#130F0E]/50 backdrop-blur-[2px]"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            className="mt-16 sm:mt-0 w-full max-w-md max-h-[min(80vh,520px)] flex flex-col bg-white rounded-2xl shadow-xl border border-[#E5E5E5] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5] shrink-0">
              <h2
                id={dialogTitleId}
                className="text-lg font-bold text-[#130F0E]"
              >
                {t("notifications.title")}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg text-[#6B7280] hover:bg-[#F4F4F4] hover:text-[#130F0E]"
                aria-label={t("notifications.close")}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 p-3 scrollbar-custom">
              {!customerId ? (
                <p className="text-sm text-[#6B7280] text-center py-8 px-2 leading-relaxed">
                  {t("notifications.missingCustomerId")}
                </p>
              ) : loading ? (
                <div className="space-y-3 py-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-16 rounded-xl bg-[#F4F4F4] animate-pulse"
                    />
                  ))}
                </div>
              ) : error ? (
                <p className="text-sm text-red-600 text-center py-8 px-2">
                  {error}
                </p>
              ) : items.length === 0 ? (
                <p className="text-sm text-[#6B7280] text-center py-8">
                  {t("notifications.empty")}
                </p>
              ) : (
                <ul className="space-y-2">
                  {items.map((n, idx) => {
                    const id = n.uuid?.trim() || `n-${idx}`;
                    const read = Number(n.is_read) === 1;
                    const title =
                      n.notification_details?.title?.trim() ||
                      t("notifications.fallbackTitle");
                    const body = n.notification_details?.body?.trim() ?? "";
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => void handleRowActivate(n)}
                          className={`w-full text-start rounded-xl border px-4 py-3 transition-colors ${
                            read
                              ? "border-[#E5E5E5] bg-white"
                              : "border-[#DD0428]/25 bg-[#BD2226]/5"
                          } hover:border-[#DD0428]/40`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-[#130F0E] line-clamp-2">
                              {title}
                            </span>
                            {!read ? (
                              <span className="shrink-0 w-2 h-2 rounded-full bg-[#BD2226] mt-1.5" />
                            ) : null}
                          </div>
                          {body ? (
                            <p className="text-xs text-[#6B7280] mt-1 line-clamp-3">
                              {body}
                            </p>
                          ) : null}
                          {n.created_at ? (
                            <p className="text-[10px] text-[#9CA3AF] mt-2">
                              {new Date(n.created_at).toLocaleString(
                                lang === "ar" ? "ar-SA" : "en-US",
                                {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                }
                              )}
                            </p>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
